import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Bed, Bath, Maximize, Phone, Mail, MessageCircle, Camera, Calendar, LandPlot, Compass, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { CallContactDialog } from './CallContactDialog';
import { EmailContactDialog } from './EmailContactDialog';
import { formatDistanceToNow } from 'date-fns';
import { optimizedImageUrl, optimizedSrcSet } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';

interface PropertyCardProps {
  property: any;
  layout?: 'grid' | 'list';
  cardType?: 'property' | 'project' | 'plot' | 'commercial';
}

export function PropertyCard({ property, layout = 'grid', cardType = 'property' }: PropertyCardProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saved, setSaved] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const lang = i18n.language || 'en';
  const title = typeof property.title === 'object' ? (property.title[lang] || property.title.en || '') : (property.title || property.name || '');
  const description = typeof property.description === 'object' ? (property.description?.[lang] || property.description?.en || '') : (property.description || '');
  const images = cardType === 'plot'
    ? (property.plot_images || [])
    : (property.property_images || property.project_images || []);
  const primaryImageRaw = images.find((img: any) => img.is_primary)?.url
    || images[0]?.url
    || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop';
  // Grid card ~400px wide, list card ~360px wide; request ~480px (covers 2x on small/retail).
  const gridImage = optimizedImageUrl(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const gridSrcSet = optimizedSrcSet(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const listImage = optimizedImageUrl(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const listSrcSet = optimizedSrcSet(primaryImageRaw, { width: 480, height: 360, quality: 75 });
  const imageCount = images.length;
  const agentLogo = property.agent_logo_url || property.agency_logo_url || property.logo_url || null;
  const features = property.features as any;
  const commercialSubType = features?.commercial_sub_type;

  // Featured tier (super_hot > hot > standard) from Ad Ops
  const tier = property.featured_tier as 'super_hot' | 'hot' | 'standard' | null | undefined;
  const tierBadge = tier === 'super_hot'
    ? { label: '🔥 Super Hot', cls: 'bg-destructive text-destructive-foreground' }
    : tier === 'hot'
    ? { label: '🔥 Hot', cls: 'bg-orange-500 text-white' }
    : tier === 'standard'
    ? { label: '⭐ Featured', cls: 'bg-amber-500 text-white' }
    : property.is_featured
    ? { label: t('property.featured'), cls: 'bg-secondary text-secondary-foreground' }
    : null;

  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) =>
    fmtPrice(price, currency, displayCurrency, rate);

  const timeAgo = property.created_at
    ? formatDistanceToNow(new Date(property.created_at), { addSuffix: true })
    : '';

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({ variant: 'destructive', title: 'Please login to save properties' });
      return;
    }
    if (saved) {
      await supabase.from('favorites').delete().eq('property_id', property.id).eq('user_id', user.id);
      setSaved(false);
    } else {
      await supabase.from('favorites').insert({ property_id: property.id, user_id: user.id });
      setSaved(true);
    }
  };

  const handleAction = (e: React.MouseEvent, action: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (action === 'call' && property.contact_phone) {
      window.open(`tel:${property.contact_phone}`, '_self');
    } else if (action === 'whatsapp' && property.contact_phone) {
      window.open(`https://wa.me/${property.contact_phone.replace(/[^0-9]/g, '')}`, '_blank');
    } else if (action === 'email' && property.contact_email) {
      window.open(`mailto:${property.contact_email}`, '_self');
    } else {
      toast({ title: 'Contact info not available' });
    }
  };

  const detailLink = cardType === 'project'
    ? `/projects/${property.id}`
    : cardType === 'plot'
    ? `/plot/${property.id}`
    : cardType === 'commercial'
    ? `/commercial/${property.id}`
    : `/property/${property.id}`;

  // ─── GRID CARD ───────────────────────────────────────────────
  if (layout === 'grid') {
    return (
      <Link to={detailLink} className="group border rounded-xl bg-card overflow-hidden hover:shadow-md transition-all">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={gridImage} srcSet={gridSrcSet} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          <Badge className="absolute top-2.5 left-2.5 text-[10px] font-semibold">
            {cardType === 'project' ? (property.status?.replace('_', ' ') || 'Project') : (property.listing_type === 'sale' ? t('property.forSale') : t('property.forRent'))}
          </Badge>
          {commercialSubType && cardType === 'commercial' && <Badge variant="secondary" className="absolute top-2.5 right-10 text-[10px] capitalize">{commercialSubType}</Badge>}
          {tierBadge && <Badge className={`absolute top-2.5 right-10 text-[10px] font-bold uppercase tracking-wide ${tierBadge.cls}`}>{tierBadge.label}</Badge>}
          <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-white/80 hover:bg-white rounded-full" onClick={handleSave}>
            <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-red-500 text-red-500' : ''}`} />
          </Button>
          {imageCount > 1 && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
              <Camera className="h-3 w-3" />{imageCount}
            </div>
          )}
        </div>
        <div className="p-3.5">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-base font-bold text-primary">
              {cardType === 'project' && property.price_min
                ? `${formatPrice(property.price_min, property.currency || 'ETB')} - ${formatPrice(property.price_max || property.price_min, property.currency || 'ETB')}`
                : formatPrice(property.price, property.currency || 'ETB')}
            </span>
            {property.listing_type === 'rent' && <span className="text-xs text-muted-foreground">/mo</span>}
          </div>
          <h3 className="font-medium text-sm mt-1 line-clamp-1 group-hover:text-primary transition-colors">{title}</h3>
          <div className="flex items-center gap-1 text-muted-foreground text-xs mt-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{property.city}{property.sub_city ? `, ${property.sub_city}` : ''}</span>
          </div>
          <div className="flex gap-3 mt-3 pt-2.5 border-t text-xs text-muted-foreground">
            {cardType === 'plot' ? (
              <>
                {property.area_sqm != null && <span className="flex items-center gap-1"><LandPlot className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
                {property.plot_purpose && <span className="capitalize">{property.plot_purpose}</span>}
                {property.facing_direction && <span className="flex items-center gap-1"><Compass className="h-3.5 w-3.5" />{property.facing_direction.replace('_', ' ')}</span>}
              </>
            ) : cardType === 'commercial' ? (
              <>
                {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
                {property.total_floors != null && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{property.total_floors} floors</span>}
              </>
            ) : (
              <>
                {property.bedrooms != null && <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" />{property.bedrooms}</span>}
                {property.bathrooms != null && <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{property.bathrooms}</span>}
                {property.area_sqm != null && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{property.area_sqm}m²</span>}
              </>
            )}
          </div>
          {cardType === 'plot' && (
            <div className="flex gap-1.5 flex-wrap mt-1">
              {property.is_corner && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Corner</Badge>}
              {property.is_park_face && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Park Face</Badge>}
              {property.is_main_road && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Main Road</Badge>}
            </div>
          )}
        </div>
      </Link>
    );
  }

  // ─── LIST CARD (Zameen-style) ────────────────────────────────
  return (
    <>
    <div className="group border rounded-xl bg-card overflow-hidden hover:shadow-lg transition-all">
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <Link to={detailLink} className="relative sm:w-[320px] md:w-[360px] h-[220px] sm:h-auto shrink-0 overflow-hidden">
          <img src={listImage} srcSet={listSrcSet} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
          {tierBadge && (
            <Badge className={`absolute top-2.5 left-2.5 text-[10px] font-bold uppercase tracking-wide ${tierBadge.cls}`}>
              {tierBadge.label}
            </Badge>
          )}
          {imageCount > 1 && (
            <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
              <Camera className="h-3.5 w-3.5" />{imageCount}
            </div>
          )}
          <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5">
            <button onClick={(e) => { e.preventDefault(); }} className="h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button onClick={handleSave} className="h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors">
              <Heart className={`h-3.5 w-3.5 ${saved ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
            </button>
          </div>
        </Link>

        {/* Content */}
        <div className="flex-1 flex flex-col p-4 min-w-0">
          <Link to={detailLink} className="flex-1 min-w-0">
            {/* Location */}
            <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
              <MapPin className="h-3 w-3 shrink-0 text-primary" />
              <span className="truncate">{property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}</span>
            </div>

            {/* Title */}
            <h3 className="font-semibold text-base md:text-lg group-hover:text-primary transition-colors line-clamp-1 mb-1.5">{title}</h3>

            {/* Price */}
            <div className="flex items-baseline gap-1.5 mb-2.5">
              {cardType === 'project' && <span className="text-xs text-muted-foreground">Starting from</span>}
              <span className="font-display text-xl font-bold text-foreground">
                {cardType === 'project' && property.price_min
                  ? formatPrice(property.price_min, property.currency || 'ETB')
                  : formatPrice(property.price, property.currency || 'ETB')}
              </span>
              {property.listing_type === 'rent' && <span className="text-sm text-muted-foreground">/mo</span>}
            </div>

            {/* Stats row */}
            <div className="flex gap-5 text-sm text-muted-foreground mb-2">
              {cardType === 'plot' ? (
                <>
                  {property.area_sqm != null && <span className="flex items-center gap-1.5"><LandPlot className="h-4 w-4" />{property.area_sqm} m²</span>}
                  {property.plot_purpose && <span className="flex items-center gap-1.5 capitalize">{property.plot_purpose}</span>}
                  {property.facing_direction && <span className="flex items-center gap-1.5"><Compass className="h-4 w-4" />{property.facing_direction.replace('_', ' ')}</span>}
                </>
              ) : cardType === 'commercial' ? (
                <>
                  {property.area_sqm != null && <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" />{property.area_sqm} m²</span>}
                  {property.total_floors != null && <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" />{property.total_floors} floors</span>}
                  {commercialSubType && <Badge variant="secondary" className="text-xs">{commercialSubType}</Badge>}
                </>
              ) : (
                <>
                  {property.bedrooms != null && <span className="flex items-center gap-1.5"><Bed className="h-4 w-4" />{property.bedrooms}</span>}
                  {property.bathrooms != null && <span className="flex items-center gap-1.5"><Bath className="h-4 w-4" />{property.bathrooms}</span>}
                  {property.area_sqm != null && <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" />{property.area_sqm} m²</span>}
                </>
              )}
              {cardType === 'project' && property.total_units && (
                <span className="flex items-center gap-1.5 text-xs">{property.total_units} units</span>
              )}
            </div>
            {cardType === 'plot' && (
              <div className="flex gap-1.5 flex-wrap mb-2">
                {property.is_corner && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Corner</Badge>}
                {property.is_park_face && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Park Face</Badge>}
                {property.is_main_road && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Main Road</Badge>}
              </div>
            )}

            {/* Description snippet */}
            {description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{description}</p>
            )}

            {/* Timestamp */}
            {timeAgo && (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70 mb-3">
                <Calendar className="h-3 w-3" />
                Added {timeAgo}
              </div>
            )}
          </Link>

          {/* Action buttons + Agent/Agency logo */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="flex items-center gap-2">
              {cardType === 'project' ? (
                <>
                  <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs px-4" asChild>
                    <Link to={detailLink} onClick={(e) => e.stopPropagation()}>
                      Reserve
                    </Link>
                  </Button>
                  <Button size="sm" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-xs px-4" onClick={(e) => handleAction(e, 'call')}>
                    <Phone className="h-3.5 w-3.5 mr-1" />Call
                  </Button>
                  <Button size="sm" variant="outline" className="font-semibold text-xs px-4" onClick={(e) => handleAction(e, 'email')}>
                    <Mail className="h-3.5 w-3.5 mr-1" />Email
                  </Button>
                </>
              ) : (
                <>
                  <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEmailDialogOpen(true); }}>
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button size="sm" className="bg-whatsapp hover:bg-whatsapp/90 text-whatsapp-foreground font-semibold text-xs px-4" onClick={(e) => handleAction(e, 'whatsapp')}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp
                  </Button>
                  <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-xs px-4" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCallDialogOpen(true); }}>
                    <Phone className="h-3.5 w-3.5 mr-1" />Call
                  </Button>
                </>
              )}
            </div>

            {/* Agent/Agency logo */}
            {agentLogo && (
              <div className="shrink-0 ml-3">
                <img src={agentLogo} alt="Agent" className="h-9 w-auto max-w-[80px] object-contain rounded opacity-70 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Contact Dialogs */}
    <CallContactDialog
      open={callDialogOpen}
      onOpenChange={setCallDialogOpen}
      agentName={property.contact_person || 'Property Agent'}
      agencyName={property.agency_name || property.developer_name}
      mobileNumber={property.contact_phone}
      phoneNumber={property.contact_phone}
      referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
    />
    <EmailContactDialog
      open={emailDialogOpen}
      onOpenChange={setEmailDialogOpen}
      agentName={property.contact_person || 'Property Agent'}
      agencyName={property.agency_name || property.developer_name}
      agentLogo={agentLogo}
      phoneNumber={property.contact_phone}
      referenceId={property.reference_code || `ID${property.id?.slice(0, 8)}`}
      propertyId={cardType === 'property' ? property.id : undefined}
      projectId={cardType === 'project' ? property.id : undefined}
    />
    </>
  );
}

